import { useContext, useEffect, useState } from "react"
import { useColorScheme } from '@mui/material/styles'
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom"
import { AppContext } from "/providers/app-context"
import Analytics from '/components/google-analytics.js'

import BillingPaymentCard from '/pages/billing-payment-card'
import BillingDirectDebit from '/pages/billing-direct-debit'
import BillingInvoices from '/pages/billing-invoices'
import BillingBuyCredits from '/pages/billing-buy-credits'
import BillingContractApplication from '/pages/billing-credit-plan'
import BillingCreditUsage from '/pages/billing-credit-usage'
import SupportHistory from '/pages/support-history'
import SupportThread from './pages/support-thread'
import SupportForm from '/pages/support-form'
import Dashboard from '/pages/dashboard'
import Loading from '/pages/loading'
import Login from '/pages/login'
import ResetPassword from '/pages/reset-password'
import AcceptInvitation from '/pages/accept-invitation'
import PassDetails from '/pages/pass-details'
import PassNotifications from '/pages/pass-notifications'
import PassEditor from '/pages/pass-editor'
import PassScanActions from '/pages/pass-scan-actions'
import PassScannerHistory from '/pages/pass-scans'
import PassInstaller from '/pages/pass-installer'
import DownloadScannerApps from '/pages/download-scanner-apps'
import CreateNewTemplate from '/pages/create-new-template'
import TemplateList from '/pages/template-list'
import TemplateDetails from '/pages/template-details'
import TemplateIssuedPasses from '/pages/template-issued-passes'
import TemplateDuplicator from '/pages/template-duplicator'
import TemplateEditor from '/pages/template-editor'
import TemplateStats from '/pages/template-stats'
import TemplateScanReportDates from '/pages/template-scan-report-dates'
import TemplateScanReport from './pages/template-scan-report'
import TemplateIssuePasses from '/pages/template-issue-passes'
import AccountManagementDetails from '/pages/account-management-details'
import AccountManagementContact from '/pages/account-management-contact'
import AccountManagementCertificates from '/pages/account-management-certificates'
import AccountManagementClients from '/pages/account-management-clients'
import AccountManagementUsers from '/pages/account-management-users'
import AccountManagementDelete from '/pages/account-management-delete'
import AccountManagementPassword from '/pages/account-management-password'
import AccountManagementapiKey from '/pages/account-management-api-keys'
import ActivePasses from '/pages/active-passes'
import Statistics from '/pages/statistics'
import StatisticsPassActivity from '/pages/statistics-pass-activity'
import StatisticsAdoption from '/pages/statistics-adoption'
import StatisticsTemplateChanges from '/pages/statistics-template-changes'
import StatisticsInstalls from '/pages/statistics-installs'
import StatisticsScans from '/pages/statistics-scans'
import StatisticsPassTypes from '/pages/statistics-pass-types'
import Onboarding from '/pages/onboarding'



const AppRoutes = ({ client, loading }) => {

  const { mode, systemMode } = useColorScheme()
  const [routes, setRoutes] = useState([<Route key="loading" path="*" element={<Loading />} />])
  const { user } = useContext(AppContext)

  useEffect(() => {
    const themeMode = mode === "system" ? systemMode : mode
    document.body.setAttribute('data-theme', themeMode)
    if (themeMode === 'dark') {
      document.body.classList.add('dark')
    } else {
      document.body.classList.remove('dark')
    }
  }, [mode, systemMode])

  useEffect(() => {
    if (!loading) {
      if (client === null) {
        setRoutes([
          <Route key="reset-password" path="reset-password/:token" element={<ResetPassword/>}/>,
          <Route key="accept-invitation" path="accept-invite/:token" element={<AcceptInvitation/>}/>,
          <Route key="login" path="*" element={<Login />} />,
        ])
      } else if (!client.initialised) {
        setRoutes([
          <Route key="onboarding" path="*" element={<Onboarding />} />,
        ])
      } else {
        setRoutes([
          <Route key="root" path="/">
            <Route index element={<Dashboard />} />,
            <Route path="account-management">
              <Route index element={<Navigate to={user?.permissions.client_admin ? "details" : "password"} replace />} />
              <Route path="details" element={<AccountManagementDetails />} />
              <Route path="contact" element={<AccountManagementContact />} />
              <Route path="certificates" element={<AccountManagementCertificates />} />
              <Route path="users" element={<AccountManagementUsers />} />
              <Route path="clients" element={<AccountManagementClients />} />
              <Route path="api-keys" element={<AccountManagementapiKey />} />
              <Route path="password" element={<AccountManagementPassword />} />
              <Route path="delete" element={<AccountManagementDelete />} />
            </Route>,

            <Route path="billing">
              {client.billing_mode === "prepay" && <Route index element={<Navigate to="buy-credits" replace />} />}
              {client.billing_mode === "postpay" && <Route index element={<Navigate to="invoices" replace />} />}
              <Route path="invoices" element={<BillingInvoices />} />
              <Route path="credit-usage" element={<BillingCreditUsage />} />
              <Route path="payment-card" element={<BillingPaymentCard />} />
              <Route path="direct-debit" element={<BillingDirectDebit />} />
              <Route path="buy-credits" element={<BillingBuyCredits />} />
              <Route path="credit-plan" element={<BillingContractApplication />} />
            </Route>,

            <Route path="create-template" element={<CreateNewTemplate />} />,
            <Route path="active-passes" element={<ActivePasses />} />,
            <Route path="statistics">
              <Route index element={<Statistics />} />
              <Route path="pass-activity" element={<StatisticsPassActivity />} />
              <Route path="adoption" element={<StatisticsAdoption />} />
              <Route path="template-changes" element={<StatisticsTemplateChanges />} />
              <Route path="installs" element={<StatisticsInstalls />} />
              <Route path="scans" element={<StatisticsScans />} />
              <Route path="pass-types" element={<StatisticsPassTypes />} />
            </Route>,
            <Route path="download-apps" element={<DownloadScannerApps />} />,
 
            <Route path="support">
              <Route index element={<Navigate to="history" replace />} />
              <Route path="history" element={<SupportHistory />} />
              <Route path="thread/:thread_id" element={<SupportThread />} />,
              <Route path="new" element={<SupportForm />} />
            </Route>

            <Route path="templates">
              <Route index element={<TemplateList />} />
              <Route path=":templateId">
                <Route index element={<Navigate to="details" replace />} />
                <Route path="details" element={<TemplateDetails />} />
                <Route path="edit" element={<TemplateEditor />} />
                <Route path="duplicate" element={<TemplateDuplicator />} />
                <Route path="issue-passes" element={<TemplateIssuePasses />} />
                <Route path="stats" element={<TemplateStats />} />

                <Route path="scan-reports">
                  <Route index element={<TemplateScanReportDates />} />
                  <Route path=":ts" element={<TemplateScanReport />} />
                </Route>

                <Route path="passes">
                  <Route index element={<TemplateIssuedPasses />} />
                  <Route path=":passId">
                    <Route index element={<Navigate to="details" replace />} />
                    <Route path="details" element={<PassDetails />} />
                    <Route path="messages" element={<PassNotifications />} />
                    <Route path="edit" element={<PassEditor />} />
                    <Route path="install" element={<PassInstaller />} />
                    <Route path="scan-actions" element={<PassScanActions />} />
                    <Route path="scans" element={<PassScannerHistory />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>,

          <Route key="catchall" path="*" element={<Navigate to="/" replace />} />
        ])
      }
    }
  }, [client, loading])




  return (
    <BrowserRouter>
      <Analytics/>
      <Routes>
        {routes}
      </Routes>
    </BrowserRouter>
  )
}

export default AppRoutes
